import {
  Button,
  InlineLoading,
  InlineNotification,
  FlexGrid as Grid,
  Row,
  Column,
  DefinitionTooltip,
  Tile,
} from '@carbon/react';
import {
  Close,
  MapBoundary,
  CenterCircle,
  Information,
} from '@carbon/react/icons';
import { Well as WellIcon } from 'components/icons/well';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { mapRefAtom } from 'components/map/map';
import { atom, useAtom, useAtomValue } from 'jotai';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { PackageResponse } from 'types/packages-api/responses';
import { PackagesMap } from '../map/packages-map';
import { ProvideRootResource } from 'components/hydrate-atoms';
import centroid from '@turf/centroid';

import style from './map-isolator.module.scss';
import { createResourceAtom } from 'lib/atoms';
import { WellsResponse } from 'types/api-responses';
import { WellPageContent } from 'components/well-page-content';
import { useWellCompletions } from 'lib/wells';
import { getMapDefaultConfig } from 'components/map/atoms';
import { LinkWithHover } from 'components/hover-card-resource';
import { DebouncedInput } from 'components/debounced-input';
import { EmptyState } from 'components/empty-state';
import {
  wellsV2LayerAtom,
  wellsV2HorizontalProducingLayerAtom,
  wellsV2HorizontalDucLayerAtom,
  wellsV2HorizontalPermitLayerAtom,
  wellsV2HorizontalShutInLayerAtom,
  wellsV2HorizontalExpiredPermitLayerAtom,
  wellsV2HorizontalInactiveLayerAtom,
  wellsV2HorizontalNonDerivedEnabledAtom,
  wellsV2VerticalProducingLayerAtom,
  wellsV2VerticalDucLayerAtom,
  wellsV2VerticalPermitLayerAtom,
  wellsV2VerticalShutInLayerAtom,
  wellsV2VerticalExpiredPermitLayerAtom,
  wellsV2VerticalInactiveLayerAtom,
  wellsV2VerticalNonDerivedEnabledAtom,
} from '../map/atoms';

export type MapPoints = Record<
  string,
  {
    id: number;
    href: string;
    name: string | null;
    wells: {
      uwi: string;
      operator: string | null;
      name: string | null;
    }[];
  }[]
>;

const assetsLayerAtom = atom(true);
const developmentAreaLayerAtom = atom(true);
const titleLayerAtom = atom(true);

function sortKeysByWellCount(obj: MapPoints) {
  // Convert object keys to an array of [key, totalWells] pairs
  const pairs = Object.keys(obj).map((key) => {
    const totalWells = obj[key].reduce(
      (acc, current) => acc + current.wells.length,
      0
    );
    return [key, totalWells] as const;
  });

  // Sort the pairs by totalWells, in descending order
  return pairs.sort((a, b) => b[1] - a[1]);
}

function sortKeysByUnitCount(obj: MapPoints) {
  const pairs = Object.keys(obj).map((key) => {
    return [key, obj[key].length] as const;
  });

  // Sort by number of units, in descending order
  return pairs.sort((a, b) => b[1] - a[1]);
}

const mapConfigAtom = atom(
  getMapDefaultConfig({
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
  })
);

const layersToFilter = [
  'package-wells-v2-horizontal-survey',
  'package-wells-v2-horizontal-survey-names',
  'package-wells-v2-horizontal-stick',
  'package-wells-v2-horizontal-stick-names',
  'package-wells-v2-horizontal-producing-surface',
  'package-wells-v2-horizontal-duc-surface',
  'package-wells-v2-horizontal-permit-surface',
  'package-wells-v2-horizontal-shut-in-surface',
  'package-wells-v2-horizontal-expired-permit-surface',
  'package-wells-v2-horizontal-inactive-surface',
  'package-wells-v2-horizontal-non-derived-surface',
  'package-wells-v2-horizontal-producing-bottom',
  'package-wells-v2-horizontal-duc-bottom',
  'package-wells-v2-horizontal-permit-bottom',
  'package-wells-v2-horizontal-shut-in-bottom',
  'package-wells-v2-horizontal-expired-permit-bottom',
  'package-wells-v2-horizontal-inactive-bottom',
  'package-wells-v2-horizontal-non-derived-bottom',
  'package-wells-v2-vertical-survey',
  'package-wells-v2-vertical-survey-names',
  'package-wells-v2-vertical-stick',
  'package-wells-v2-vertical-stick-names',
  'package-wells-v2-vertical-producing-surface',
  'package-wells-v2-vertical-duc-surface',
  'package-wells-v2-vertical-permit-surface',
  'package-wells-v2-vertical-shut-in-surface',
  'package-wells-v2-vertical-expired-permit-surface',
  'package-wells-v2-vertical-inactive-surface',
  'package-wells-v2-vertical-non-derived-surface',
  'package-wells-v2-vertical-producing-bottom',
  'package-wells-v2-vertical-duc-bottom',
  'package-wells-v2-vertical-permit-bottom',
  'package-wells-v2-vertical-shut-in-bottom',
  'package-wells-v2-vertical-expired-permit-bottom',
  'package-wells-v2-vertical-inactive-bottom',
  'package-wells-v2-vertical-non-derived-bottom',
];

const MapIsolator = ({
  packageData,
  mapPoints,
  type,
}: {
  packageData: PackageResponse;
  mapPoints: MapPoints;
  type: 'slots' | 'units';
}) => {
  const [open, setOpen] = useReducer((s) => !s, false);
  const [packageMapId, triggerMapRender] = useReducer((s) => s + 1, 0);

  return (
    <>
      <Button
        size="md"
        kind="tertiary"
        hasIconOnly
        onClick={setOpen}
        iconDescription={'Map Tools'}
        tooltipAlignment="center"
        tooltipPosition="top"
        renderIcon={MapBoundary}
      />
      <Drawer
        lockBackgroundScroll
        open={open}
        direction="bottom"
        size={'90%'}
        onClose={setOpen}
        className={style.mapIsolatorDrawer}
      >
        <DrawerHeader>
          <div className={style.drawerHeader}>
            <h3>Map Tools</h3>
            <Button
              hasIconOnly
              size="lg"
              kind="ghost"
              onClick={setOpen}
              iconDescription={'Close'}
              tooltipAlignment="center"
              tooltipPosition="bottom"
              renderIcon={(props) => <Close size={20} {...props} />}
            />
          </div>
        </DrawerHeader>
        <DrawerContent>
          {packageData.boundingBox && open && (
            <ProvideRootResource>
              <>
                <PackagesMap
                  packageMapId={packageMapId}
                  boundingBox={packageData.boundingBox}
                  mapSources={packageData.resources.maps}
                  layerAtoms={{
                    devAreas: developmentAreaLayerAtom,
                    assets: assetsLayerAtom,
                    title: titleLayerAtom,
                    wellsV2: wellsV2LayerAtom,
                  }}
                  customMapConfigAtom={mapConfigAtom}
                  hideWellsLayer={false}
                />

                <div className={style.contentHeight}>
                  <MapElements
                    mapPoints={mapPoints}
                    reRenderMap={triggerMapRender}
                    type={type}
                  />
                </div>
              </>
            </ProvideRootResource>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};

const MapElements = ({
  mapPoints,
  reRenderMap,
  type,
}: {
  mapPoints: MapPoints;
  reRenderMap: () => void;
  type: 'slots' | 'units';
}) => {
  const [filterBy, setFilterBy] = useState('');
  const mapRef = useAtomValue(mapRefAtom);
  const [assetMap, setAssetMap] = useState<
    Record<string, GeoJSON.Geometry> | undefined
  >(undefined);
  const eventInstalled = useRef(false);
  const firstAssetSelected = useRef(false);
  const [, setVisibleDevAreas] = useAtom(developmentAreaLayerAtom);
  const [, setVisibleAssets] = useAtom(assetsLayerAtom);
  const [, setMapConfig] = useAtom(mapConfigAtom);

  // Add a ref to store the current isolated wells for reapplication
  const isolatedWellsRef = useRef<string[]>([]);

  const [currentTract, setCurrentTract] = useState<string | undefined>(
    undefined
  );
  const [currentDevArea, setCurrentDevArea] = useState<string | undefined>(
    undefined
  );
  const [currentWell, setCurrentWell] = useState<string | undefined>(undefined);

  const filteredAssets = useMemo(() => {
    const assets =
      type === 'slots'
        ? sortKeysByWellCount(mapPoints)
        : sortKeysByUnitCount(mapPoints);
    return assets.filter(([key]) => key.toLowerCase().includes(filterBy));
  }, [filterBy, mapPoints, type]);

  useEffect(() => {
    return () => {
      setVisibleAssets(true);
      setVisibleDevAreas(true);
    };
  }, [setVisibleAssets, setVisibleDevAreas]);

  const onRecenterMap = useCallback(() => {
    reRenderMap();
    setCurrentTract(undefined);
    setCurrentDevArea(undefined);
    setCurrentWell(undefined);
    setVisibleAssets(true);
    setVisibleDevAreas(true);
    setMapConfig((current) => ({
      ...current,
      wells: {
        checked: true,
        options: {
          surfaceHole: true,
          bottomHole: true,
          stick: true,
          survey: true,
          vertical: true,
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedDevAreas = currentTract ? mapPoints[currentTract] : [];
  const selectedWells = currentDevArea
    ? (selectedDevAreas.find((el) => el.name === currentDevArea)?.wells ?? [])
    : [];

  // Get current filter values from atoms
  const horizontalProducingEnabled = useAtomValue(
    wellsV2HorizontalProducingLayerAtom
  );
  const horizontalDucEnabled = useAtomValue(wellsV2HorizontalDucLayerAtom);
  const horizontalPermitEnabled = useAtomValue(
    wellsV2HorizontalPermitLayerAtom
  );
  const horizontalShutInEnabled = useAtomValue(
    wellsV2HorizontalShutInLayerAtom
  );
  const horizontalExpiredPermitEnabled = useAtomValue(
    wellsV2HorizontalExpiredPermitLayerAtom
  );
  const horizontalInactiveEnabled = useAtomValue(
    wellsV2HorizontalInactiveLayerAtom
  );
  const horizontalNonDerived = useAtomValue(
    wellsV2HorizontalNonDerivedEnabledAtom
  );

  const verticalProducingEnabled = useAtomValue(
    wellsV2VerticalProducingLayerAtom
  );
  const verticalDucEnabled = useAtomValue(wellsV2VerticalDucLayerAtom);
  const verticalPermitEnabled = useAtomValue(wellsV2VerticalPermitLayerAtom);
  const verticalShutInEnabled = useAtomValue(wellsV2VerticalShutInLayerAtom);
  const verticalExpiredPermitEnabled = useAtomValue(
    wellsV2VerticalExpiredPermitLayerAtom
  );
  const verticalInactiveEnabled = useAtomValue(
    wellsV2VerticalInactiveLayerAtom
  );
  const verticalNonDerived = useAtomValue(wellsV2VerticalNonDerivedEnabledAtom);

  // Function to build dynamic status filters based on enabled status checkboxes
  const buildStatusFilters = useCallback(() => {
    /**
     * The statuses each enabled toggle shows; each Create New Well status has its own toggle
     * (INS-13300).
     * @param {Array} enabled - [toggle on, statuses it shows] pairs.
     * @return {Array} One `==` status condition per shown status.
     */
    const conditionsFor = (enabled: [boolean, readonly string[]][]) =>
      enabled
        .filter(([on]) => on)
        .flatMap(([, statuses]) =>
          statuses.map((status) => ['==', 'status', status])
        );
    const horizontalStatusConditions = conditionsFor([
      [horizontalProducingEnabled, ['producing']],
      [horizontalDucEnabled, ['duc']],
      [horizontalPermitEnabled, ['permit']],
      [horizontalShutInEnabled, ['shut_in']],
      [horizontalExpiredPermitEnabled, ['expired_permit']],
      [horizontalInactiveEnabled, ['inactive']],
      [true, horizontalNonDerived],
    ]);
    const verticalStatusConditions = conditionsFor([
      [verticalProducingEnabled, ['producing']],
      [verticalDucEnabled, ['duc']],
      [verticalPermitEnabled, ['permit']],
      [verticalShutInEnabled, ['shut_in']],
      [verticalExpiredPermitEnabled, ['expired_permit']],
      [verticalInactiveEnabled, ['inactive']],
      [true, verticalNonDerived],
    ]);

    return {
      horizontal:
        horizontalStatusConditions.length > 0
          ? ['any', ...horizontalStatusConditions]
          : ['==', 'status', 'none_matching'], // No statuses will match this
      vertical:
        verticalStatusConditions.length > 0
          ? ['any', ...verticalStatusConditions]
          : ['==', 'status', 'none_matching'], // No statuses will match this
    };
  }, [
    horizontalProducingEnabled,
    horizontalDucEnabled,
    horizontalPermitEnabled,
    horizontalShutInEnabled,
    horizontalExpiredPermitEnabled,
    horizontalInactiveEnabled,
    horizontalNonDerived,
    verticalProducingEnabled,
    verticalDucEnabled,
    verticalPermitEnabled,
    verticalShutInEnabled,
    verticalExpiredPermitEnabled,
    verticalInactiveEnabled,
    verticalNonDerived,
  ]);

  const isolateWells = useCallback(
    (well: string[]) => {
      if (!mapRef?.getMap()) return;

      const map = mapRef.getMap();

      // Store the isolated wells in the ref for future reapplication
      isolatedWellsRef.current = well;

      // Helper function to safely set a filter only if the layer exists
      const safeSetFilter = (layerId: string, filter: any[]) => {
        if (!map.getLayer(layerId)) {
          return;
        }

        // Always replace the UWI filter entirely because if you leave an empty UWI filter in place,
        // the layer will not render when you try to filter another set of UWIs.
        const existingFilters = map.getFilter(layerId).filter((f) => {
          if (!Array.isArray(f)) {
            return true;
          }

          if (f.length < 2) {
            return true;
          }

          if (f[0] === 'in' && f[1] === 'uwi') {
            return false;
          }

          return true;
        });

        if (existingFilters) {
          map.setFilter(layerId, [...existingFilters, filter]);
        } else {
          map.setFilter(layerId, filter);
        }
      };

      layersToFilter.forEach((layer) => {
        safeSetFilter(layer, ['in', 'uwi', ...well]);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapRef, buildStatusFilters]
  );

  // Add a handler function to respond to filter changes
  const handleFilterChange = useCallback(() => {
    // If we have isolated wells and a tract selected, reapply the isolation with current filter settings
    if (isolatedWellsRef.current.length > 0 && currentTract) {
      isolateWells(isolatedWellsRef.current);
    }
  }, [isolateWells, currentTract]);

  // Listen for filter changes and reapply isolation
  useEffect(() => {
    handleFilterChange();
  }, [handleFilterChange]);

  const isolateDevAreas = useCallback(
    (areas: string[]) => {
      if (!mapRef?.getMap()) return;

      mapRef
        .getMap()
        .setFilter('development-area-fill-scoped-package-isolated', [
          'in',
          'name',
          ...areas,
        ]);
      mapRef
        .getMap()
        .setFilter('development-area-line-scoped-package-isolated', [
          'in',
          'name',
          ...areas,
        ]);
    },
    [mapRef]
  );

  const highlightAsset = useCallback(
    (asset: string) => {
      if (!mapRef?.getMap()) return;

      mapRef.getMap().moveLayer('package-assets-line-isolated-highlighted');
      mapRef
        .getMap()
        .setFilter('package-assets-line-isolated-highlighted', [
          'in',
          'name',
          asset,
        ]);
    },
    [mapRef]
  );

  // Function to select an asset
  const selectAsset = useCallback(
    (assetName: string) => {
      if (!assetMap || !assetMap[assetName] || !mapRef) return;

      const [longitude, latitude] = centroid(assetMap[assetName] as any)
        .geometry.coordinates;

      setCurrentTract(assetName);
      setCurrentDevArea(undefined);
      setCurrentWell(undefined);
      isolateWells([]);

      mapRef.flyTo({
        center: [longitude, latitude],
        zoom: 12,
      });

      setVisibleAssets(false);
      setVisibleDevAreas(false);
      setMapConfig((current) => ({
        ...current,
        wells: {
          checked: false,
          options: {
            surfaceHole: false,
            bottomHole: false,
            stick: false,
            survey: false,
            vertical: false,
          },
        },
      }));

      const devAreas = mapPoints[assetName].map((el) => el);
      isolateDevAreas(devAreas.map((el) => el.name || ''));

      // Store and apply the well isolation
      const wellUwis = devAreas.flatMap((el) => el.wells).map((el) => el.uwi);
      isolatedWellsRef.current = wellUwis;
      isolateWells(wellUwis);

      highlightAsset(assetName);

      mapRef
        .getMap()
        .setFilter('package-assets-fill-isolated', ['in', 'name', assetName]);
      mapRef
        .getMap()
        .setFilter('package-assets-line-isolated', ['in', 'name', assetName]);
    },
    [
      assetMap,
      mapRef,
      mapPoints,
      isolateWells,
      isolateDevAreas,
      highlightAsset,
      setVisibleAssets,
      setVisibleDevAreas,
      setMapConfig,
    ]
  );

  // Effect to auto-select the first asset when asset data is available
  useEffect(() => {
    if (
      !currentTract && // Only auto-select if no asset is currently selected
      assetMap &&
      Object.keys(assetMap).length > 0 &&
      filteredAssets.length > 0
    ) {
      const firstAssetName = filteredAssets[0][0];
      if (assetMap[firstAssetName]) {
        selectAsset(firstAssetName);
        // Keep setting firstAssetSelected for backward compatibility
        firstAssetSelected.current = true;
      }
    }
    // No cleanup function needed - we're using currentTract to determine selection state
  }, [assetMap, filteredAssets, selectAsset, currentTract]);

  // Setup event handler for map source loading
  useEffect(() => {
    if (!mapRef || eventInstalled.current) return;

    eventInstalled.current = true;

    const handler = () => {
      if (assetMap) return;

      if (
        mapRef.getSource('package-assets-layer-source') &&
        mapRef.isSourceLoaded('package-assets-layer-source')
      ) {
        const assets = mapRef
          .queryRenderedFeatures(undefined, {
            layers: ['package-assets-fill'],
          })
          .reduce(
            (acc, current) => {
              if (
                current.properties &&
                'coordinates' in current.geometry &&
                !acc[current.properties.name]
              ) {
                acc[current.properties.name] = current.geometry;
              }
              return acc;
            },
            {} as Record<string, any>
          );

        if (Object.keys(assets).length) {
          setAssetMap(assets);
          mapRef.off('sourcedata', handler);
        }
      }
    };

    mapRef.on('sourcedata', handler);

    return () => {
      if (mapRef) {
        mapRef.off('sourcedata', handler);
      }
    };
  }, [mapRef, assetMap]);

  if (!mapRef) return null;

  return (
    <Grid className={style.grid} fullWidth>
      <Row className={style.contentList}>
        <Column lg={5}>
          <Row className={style.mapElementsList}>
            <Column className={style.colRelative}>
              {filteredAssets.length ? (
                <Button
                  hasIconOnly
                  className={style.recenterBtn}
                  kind="primary"
                  size="sm"
                  iconDescription="Recenter"
                  tooltipAlignment="center"
                  tooltipPosition="left"
                  renderIcon={CenterCircle}
                  onClick={() => onRecenterMap()}
                />
              ) : null}
              <ul>
                <legend>Assets ({filteredAssets.length})</legend>
                <div className={style.searchContainer}>
                  <DebouncedInput
                    value={filterBy}
                    onChange={(text) => {
                      setFilterBy(text.toString().toLocaleLowerCase());
                    }}
                    placeholder="Search assets..."
                  />
                </div>

                {filteredAssets.length === 0 ? (
                  <Tile>
                    <EmptyState
                      headerText="No Assets Found"
                      helperText="Try adjusting your search to find what you're looking for."
                    />
                  </Tile>
                ) : null}
                {assetMap &&
                  filteredAssets.map(([el, wells]) => {
                    return (
                      <li
                        key={el}
                        className={currentTract === el ? style.selected : ''}
                      >
                        {assetMap[el] ? (
                          <a onClick={() => selectAsset(el)}>
                            {el} <strong>({wells})</strong>
                          </a>
                        ) : (
                          el
                        )}
                      </li>
                    );
                  })}
              </ul>
            </Column>

            <Column className={style.colRelative}>
              <ul>
                {selectedDevAreas.length ? (
                  <legend>Development Areas</legend>
                ) : null}
                {selectedDevAreas.map((area) => {
                  const wells =
                    (currentTract ? mapPoints[currentTract] : []).find(
                      (el) => el.name === area.name
                    )?.wells ?? [];

                  return (
                    <li
                      key={area.id}
                      className={
                        currentDevArea === area.name ? style.selected : ''
                      }
                    >
                      {area.name ? (
                        <LinkWithHover kind="development-area" href={area.href}>
                          <a
                            onClick={() => {
                              if (!area.name) return;
                              setCurrentDevArea(area.name);
                              setCurrentWell(undefined);
                              isolateWells(wells.map((el) => el.uwi));
                              isolateDevAreas([area.name]);
                            }}
                          >
                            {area.name} <strong>({wells.length})</strong>
                          </a>
                        </LinkWithHover>
                      ) : (
                        area.name
                      )}
                    </li>
                  );
                })}
              </ul>
            </Column>

            <Column className={style.colRelative}>
              <ul>
                {selectedWells.length ? <legend>Wells</legend> : null}
                {selectedWells.map((well) => (
                  <li
                    key={well.uwi}
                    className={currentWell === well.uwi ? style.selected : ''}
                  >
                    <a
                      onClick={() => {
                        setCurrentWell(well.uwi);
                        isolateWells([well.uwi]);
                      }}
                    >
                      <strong>{well.uwi}</strong>
                      <p>
                        <DefinitionTooltip
                          align="top-start"
                          definition="SRP Well Name"
                          openOnHover
                        >
                          <Information />
                        </DefinitionTooltip>
                        <strong>{well.name}</strong>
                      </p>
                      <p>
                        <DefinitionTooltip
                          align="top-start"
                          definition="Current Operator"
                          openOnHover
                        >
                          <Information />
                        </DefinitionTooltip>
                        <strong>{well.operator}</strong>
                      </p>
                    </a>
                  </li>
                ))}
              </ul>
            </Column>
          </Row>
        </Column>

        <Column lg={11} className={style.colScroll}>
          {currentWell && (
            <WellInfo uwi={currentWell} href={`/wells/${currentWell}`} />
          )}
        </Column>
      </Row>
    </Grid>
  );
};

const useWellAtom = (href: string) =>
  useMemo(() => createResourceAtom<WellsResponse>(href), [href]);

const WellInfo = ({ uwi, href }: { uwi: string; href: string }) => {
  const wellAtom = useWellAtom(href);
  const well = useAtomValue(wellAtom);
  // For the dates in the well's Wellbore Completions; read once the well has loaded,
  // when WellPageContent below renders.
  const { data: completions, error: completionsError } = useWellCompletions(
    uwi,
    well.data && !('error' in well.data)
      ? (well.data.resources.queries.wellCompletions?.href ?? '')
      : ''
  );

  if (well.data && 'error' in well.data)
    return (
      <InlineNotification kind="error" title={well.data.error} lowContrast />
    );

  return well.data ? (
    <>
      <div className={style.wellHeader}>
        <WellIcon medium hasBackground={true} />
        <h2 className={style.wellTitle}>{uwi}</h2>
      </div>
      <WellPageContent
        well={well.data}
        uwi={uwi}
        completions={completions}
        completionsError={completionsError}
      />
    </>
  ) : (
    <InlineLoading />
  );
};

export { MapIsolator };
